import { FormElement } from "../sections/DynamicForm/DynamicForm.types";
import { CPFValidator } from "./genericValidator";
import { documentValidator } from "./documentValidator";

const validateRequired = (
  element: FormElement,
  formData: FormData
): boolean => {
  const { type, props } = element;
  const { attribute_name, validation } = props;

  if (!validation?.required || !attribute_name) {
    return true; 
  }

  if (type === "checkbox") {
    const isGroup = props.options && props.options.length > 0;
    if (isGroup) {
      if (!validation.minSelections || validation.minSelections === 0) {
        return formData.getAll(`${attribute_name}[]`).length > 0;
      }
    } else {
      return formData.has(attribute_name);
    }
  } else {
    const value = formData.get(attribute_name);
    return !!value && (typeof value !== "string" || value.trim() !== "");
  }
  return true;
};

const validatePattern = (
  element: FormElement,
  formData: FormData
): boolean => {
  const { attribute_name, validation } = element.props;
  if (!validation?.pattern || !attribute_name) return true;

  const value = formData.get(attribute_name);
  if (!value || typeof value !== "string") return true;

  try {
    return new RegExp(validation.pattern).test(value);
  } catch (e) {
    console.error(`Invalid regex pattern for field: ${attribute_name}`, e);
    return false;
  }
};

const validateMinSelections = (
  element: FormElement,
  formData: FormData
): boolean => {
  const { type, props } = element;
  const { attribute_name, validation } = props;

  if (
    type !== "checkbox" ||
    !validation?.minSelections ||
    validation.minSelections <= 0 ||
    !attribute_name
  ) {
    return true;
  }
  return (
    formData.getAll(`${attribute_name}[]`).length >= validation.minSelections
  );
};

const typeValidators: Record<
  string,
  (value: FormDataEntryValue | null) => boolean
> = {
  "cep-input": value => {
    if (!value) return true;
    return String(value).replace(/\D/g, "").length === 8;
  },
  "cpf-input": value => {
    if (!value) return true;
    return CPFValidator(String(value));
  },
  "document-input": value => {
    if (!value) return true;
    return documentValidator(String(value));
  },
  "phone-input": value => {
    if (!value) return true;
    const normalized = String(value).replace(/\D/g, "");
    return normalized.length >= 10 && normalized.length <= 11;
  },
  "email_input": value => {
    if (!value) return true;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(String(value));
  },
  "file_upload": value => {
    if (!value) return true;
    return typeof value === 'string' && value.startsWith('documents/');
  },
};

const validateByType = (element: FormElement, formData: FormData): boolean => {
  const { type, props } = element;
  const { attribute_name } = props;
  const validator = typeValidators[type];

  if (!validator || !attribute_name) return true;

  const value = formData.get(attribute_name);
  return validator(value);
};

const validators = [
  validateRequired,
  validatePattern,
  validateMinSelections,
  validateByType,
];

const isFieldValid = (element: FormElement, formData: FormData): boolean => {
  for (const validator of validators) {
    if (!validator(element, formData)) {
      return false;
    }
  }
  return true;
};

export const validateForm = (
  elements: FormElement[],
  formData: FormData
): Record<string, boolean> => {
  const errors: Record<string, boolean> = {};

  elements.forEach(element => {
    if (element.category !== "field" || !element.props.attribute_name) {
      return;
    }

    if (!isFieldValid(element, formData)) {
      errors[element.props.attribute_name] = true;
    }
  });

  return errors;
};